import { useCallback, useEffect, useRef } from 'react';

import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';

import { toaster } from 'components/toaster/toaster';
import { useCart } from 'hooks/use-cart';
import { useCartHydration } from 'hooks/use-cart-hydration.ts';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useLoyaltyIncentivesAvailability } from 'pages/loyalty/loyalty-incentives-components/use-loyalty-incentives-availability';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { LoyaltyAppliedOffer, LoyaltyOffer } from 'state/loyalty/types';
import { isDiscountOffer } from 'state/loyalty/utils';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';

import { useLoyaltyOfferAvailabilityEvaluation } from './use-loyalty-offer-availability-evaluation';

/**
 * This hook is used to remove an expired offer from the cart and displays a toast notification to the user.
 * Once the offersFeedbackMap is available, it will check if there is an offer with an error without threshold.
 * If there is, it will remove the offer from the cart and display a toast notification to the user.
 */
export const useRemoveExpiredOfferFromCart = () => {
  const dispatch = useAppDispatch();
  const { cartEntries, removeFromCart } = useCart();
  const { cartEntries: minimalCartEntries } = useMinimalCart();
  const { isStoreChangeHydrationInProgress } = useCartHydration({
    cartEntries,
    minimalCartEntries,
  });

  const { indexedMenu } = useUnifiedMenuContext();
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();

  const toastWasDisplayed = useRef(false);

  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const appliedLoyaltyDiscountOffer = useAppSelector(
    selectors.loyalty.selectDiscountAppliedCmsOffer
  );
  const selectedOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const offersFeedbackMap = useAppSelector(selectors.loyalty.selectOfferFeedbackMap);
  const { isOfferUnavailable } = useLoyaltyOfferAvailabilityEvaluation();
  const { checkLoyaltyOfferAvailability } = useLoyaltyIncentivesAvailability();

  const removeOffer = useCallback(
    (offer: LoyaltyAppliedOffer | LoyaltyOffer) => {
      dispatch(actions.loyalty.removeAppliedOffer(offer));
      if ('loyaltyEngineId' in offer && isDiscountOffer(offer)) {
        dispatch(actions.loyalty.resetAppliedDiscountOffers());
        // Clear selectedOffer if it matches the discount offer being removed
        if (selectedOffer?.loyaltyEngineId === offer.loyaltyEngineId) {
          dispatch(actions.loyalty.setSelectedOffer(null));
        }
      } else if ('cartId' in offer && offer.cartId) {
        removeFromCart({ cartId: offer.cartId });
      }
      toaster.informative(t('removeExpiredOfferItem'));
      toastWasDisplayed.current = true;
    },
    [dispatch, removeFromCart, selectedOffer]
  );

  const handleExpiredOfferRemoval = useCallback(() => {
    if (toastWasDisplayed.current) {
      return;
    }

    const appliedOffer = appliedOffers.find(offer =>
      cartEntries.some(entry => entry.cartId === offer.cartId)
    );

    // Only one selected offer can exist in the cart.
    const offer = appliedOffer || appliedLoyaltyDiscountOffer;

    if (offer && isOfferUnavailable(offer)) {
      removeOffer(offer);
    }

    // Also check if selectedOffer is unavailable even if not in appliedOffers
    // This handles cases where discount offers may not be tracked in appliedOffers
    // Use checkLoyaltyOfferAvailability which properly checks store restrictions
    const isSelectedOfferAvailable = selectedOffer
      ? checkLoyaltyOfferAvailability(selectedOffer)
      : true;

    if (selectedOffer && !offer && !isSelectedOfferAvailable) {
      dispatch(actions.loyalty.setSelectedOffer(null));
      // Also clear the feedback map to remove any lingering errors from the removed offer
      dispatch(actions.loyalty.resetOfferFeedbackMap());
      toaster.informative(t('removeExpiredOfferItem'));
      toastWasDisplayed.current = true;
    }
  }, [
    appliedOffers,
    appliedLoyaltyDiscountOffer,
    isOfferUnavailable,
    cartEntries,
    removeOffer,
    selectedOffer,
    dispatch,
    checkLoyaltyOfferAvailability,
  ]);

  useEffect(() => {
    if (
      offersFeedbackMap &&
      !isStoreChangeHydrationInProgress &&
      (!isUnifiedMenuEnabled || indexedMenu.isLoaded())
    ) {
      handleExpiredOfferRemoval();
    }
  }, [
    offersFeedbackMap,
    handleExpiredOfferRemoval,
    isStoreChangeHydrationInProgress,
    isUnifiedMenuEnabled,
    indexedMenu,
  ]);

  // Fallback: if all offers were cleared (no applied offers, no selected offer)
  // but feedback map still has errors (e.g., store change), clear the feedback map
  useEffect(() => {
    const hasFeedback = offersFeedbackMap && Object.keys(offersFeedbackMap).length > 0;
    const noOffersSelected = appliedOffers.length === 0 && !selectedOffer;

    if (hasFeedback && noOffersSelected) {
      dispatch(actions.loyalty.resetOfferFeedbackMap());
    }
  }, [offersFeedbackMap, appliedOffers.length, selectedOffer, dispatch]);
};
